import {
  ContestParticipationDenialReason,
  ContestParticipationMetadata,
} from '@monorepo/types';

export interface ContestErrorData {
  denialReasons?: ContestParticipationDenialReason[];
  metadata?: ContestParticipationMetadata;
}

interface ErrorWithResponse {
  response?: {
    data?: {
      message?: string;
      denialReasons?: unknown;
      metadata?: unknown;
    };
  };
  data?: {
    denialReasons?: unknown;
    metadata?: unknown;
  };
  message?: string;
  error?: string;
  participationEligibility?: {
    reasonForParticipateDenial?: unknown;
    reasonsForParticipateDenial?: unknown;
    metadata?: unknown;
  };
}

const isValidDenialReason = (
  reason: unknown,
): reason is ContestParticipationDenialReason => {
  return (
    typeof reason === 'string' &&
    Object.values(ContestParticipationDenialReason).includes(
      reason as ContestParticipationDenialReason,
    )
  );
};

/**
 * Validates if reasons array contains valid ContestParticipationDenialReason values
 */
const isValidDenialReasonsArray = (
  reasons: unknown,
): reasons is ContestParticipationDenialReason[] => {
  return (
    Array.isArray(reasons) &&
    reasons.length > 0 &&
    reasons.every(isValidDenialReason)
  );
};

/**
 * Type guard to check if error is an object with properties
 */
const isErrorObject = (error: unknown): error is ErrorWithResponse => {
  return typeof error === 'object' && error !== null;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;

  if (isErrorObject(error)) {
    return (
      error?.response?.data?.message ||
      error?.message ||
      error?.error ||
      String(error)
    );
  }

  return String(error);
};

/**
 * Extracts denial reasons and metadata from error response
 * @param error - Error object from API call or service
 * @returns Object with denialReasons array and metadata if found
 */
export const extractContestErrorData = (error: unknown): ContestErrorData => {
  if (!isErrorObject(error)) {
    return {};
  }

  // Check response data (from API calls)
  const responseData = error?.response?.data || error?.data;
  if (
    responseData?.denialReasons &&
    isValidDenialReasonsArray(responseData.denialReasons)
  ) {
    return {
      denialReasons: responseData.denialReasons,
      metadata: responseData.metadata as
        | ContestParticipationMetadata
        | undefined,
    };
  }

  // Check participationEligibility (from backend validation)
  if ('participationEligibility' in error && error.participationEligibility) {
    const eligibility = error.participationEligibility;
    const denialReasons = eligibility?.reasonsForParticipateDenial;

    if (isValidDenialReasonsArray(denialReasons)) {
      return {
        denialReasons,
        metadata: eligibility?.metadata as
          | ContestParticipationMetadata
          | undefined,
      };
    }
  }

  // Fallback: parse error message for single reason (legacy support)
  const errorMessage = getErrorMessage(error);
  const match = errorMessage.match(/because:\s*([a-z_]+)/);

  if (match?.[1] && isValidDenialReason(match[1])) {
    return { denialReasons: [match[1]] };
  }

  return {};
};
